/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNews } from '../../context/NewsContext';
import { 
  RiCalendarLine, 
  RiEyeLine, 
  RiTimeLine,
  RiArrowLeftLine,
  RiShareLine,
  RiPrinterLine,
  RiFacebookFill,
  RiTwitterFill,
  RiLinkedinFill,
  RiWhatsappFill
} from 'react-icons/ri';
import './NewsDetail.css';

const NewsDetail = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const { getNews, incrementView } = useNews();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadNews();
  }, [name]);

  useEffect(() => {
    if (news) {
      incrementView(news.id);
    }
  }, [news]);

  const loadNews = async () => {
    setLoading(true);
    try {
      const result = await getNews(name);
      if (result.success) {
        setNews(result.data);
      } else {
        setError('News article not found');
      }
    } catch (err) {
      setError('Failed to load news article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: news.title,
        text: news.summary,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/assets/img/placeholder.png';
  
  if (imageUrl.startsWith('/uploads')) {
    if (process.env.NODE_ENV === 'development') {
      return `http://localhost:5000${imageUrl}`;
    }
    const apiUrl = 'https://farmo.zuddo.biz/api';
    const backendOrigin = apiUrl.replace(/\/api\/?$/, '');
    return `${backendOrigin}${imageUrl}`;
  }
  if (imageUrl.startsWith('/assets/')) {
    return imageUrl;
  }
  return imageUrl;
};

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="error-container">
        <h2>Article Not Found</h2>
        <p>{error || 'The news article you\'re looking for doesn\'t exist.'}</p>
        <button onClick={() => navigate('/news')} className="back-btn">
          <RiArrowLeftLine /> Back to News
        </button>
      </div>
    );
  }

  return (
    <div className="news-detail-page">
      <div className="detail-container">
        <div className="detail-nav">
          <button onClick={() => navigate('/news')} className="nav-back">
            <RiArrowLeftLine /> Back to News
          </button>
          <div className="nav-actions">
            <button onClick={handleShare} className="nav-action">
              <RiShareLine /> Share
            </button>
            <button onClick={handlePrint} className="nav-action">
              <RiPrinterLine /> Print
            </button>
          </div>
        </div>

        <header className="article-header">
          <div className="article-category-wrapper">
            <span className="article-category">{news.category || 'News'}</span>
            {news.featured && <span className="article-featured">FEATURED</span>}
          </div>
          
          <h1 className="article-title">{news.title}</h1>
          
          <div className="article-meta">
            <span className="meta-item">
              <RiCalendarLine />
              {formatDate(news.published_at || news.created_at)}
            </span>
            <span className="meta-item">
              <RiEyeLine />
              {news.views || 0} views
            </span>
            <span className="meta-item">
              <RiTimeLine />
              {Math.ceil((news.content?.length || 0) / 1000)} min read
            </span>
          </div>
        </header>

  
        {news.image_url && (
          <div className="article-image-container">
            <img src={getImageUrl(news.image_url)} alt={news.title} className="article-image" />
          </div>
        )}

        <div className="article-layout">
 
          <aside className="article-sidebar">
    
            {news.author_name && (
              <div className="sidebar-block">
                <h3 className="sidebar-title">About the Author</h3>
                <div className="author-info">
                  <div className="author-avatar">
                    {news.author_name.charAt(0)}
                  </div>
                  <div className="author-details">
                    <h4>{news.author_name}</h4>
                    <p>Staff Writer</p>
                  </div>
                </div>
              </div>
            )}

        
            {news.tags && news.tags.length > 0 && (
              <div className="sidebar-block">
                <h3 className="sidebar-title">Tags</h3>
                <div className="tags-wrapper">
                  {news.tags.map((tag, index) => (
                    <span key={index} className="tag-item">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="sidebar-block">
              <h3 className="sidebar-title">Share this article</h3>
              <div className="share-buttons">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn facebook"
                >
                  <RiFacebookFill />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(news.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn twitter"
                >
                  <RiTwitterFill />
                </a>
                <a 
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn linkedin"
                >
                  <RiLinkedinFill />
                </a>
                <a 
                  href={`https://wa.me/?text=${encodeURIComponent(news.title + ' ' + window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn whatsapp"
                >
                  <RiWhatsappFill />
                </a>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="article-main">
            {news.summary && (
              <div className="article-summary">
                <p>{news.summary}</p>
              </div>
            )}
            
            <div className="article-content">
              {news.content.split('\n').map((paragraph, idx) => {
                if (paragraph.trim() === '') return null;
                if (paragraph.startsWith('# ')) {
                  return <h1 key={idx}>{paragraph.replace('# ', '')}</h1>;
                }
                if (paragraph.startsWith('## ')) {
                  return <h2 key={idx}>{paragraph.replace('## ', '')}</h2>;
                }
                if (paragraph.startsWith('### ')) {
                  return <h3 key={idx}>{paragraph.replace('### ', '')}</h3>;
                }
                if (paragraph.startsWith('- ')) {
                  return <li key={idx}>{paragraph.replace('- ', '')}</li>;
                }
                return <p key={idx}>{paragraph}</p>;
              })}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;